import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as authApi from '@/api/auth'
import { ApiError, registerTokenBridge } from '@/api/client'
import type { AuthResultDto, Permission, UserDto } from '@/types/api'

const ACCESS_TOKEN_KEY = 'uobserve.accessToken'
const REFRESH_TOKEN_KEY = 'uobserve.refreshToken'
const USER_KEY = 'uobserve.user'

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* storage disabled — the session simply will not survive a reload */
  }
}

function readStoredUser(): UserDto | null {
  const raw = readStorage(USER_KEY)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && 'id' in parsed && 'permissions' in parsed) {
      return parsed as UserDto
    }
  } catch {
    /* corrupt entry — treat as signed out */
  }
  return null
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(readStorage(ACCESS_TOKEN_KEY))
  const refreshToken = ref<string | null>(readStorage(REFRESH_TOKEN_KEY))
  const user = ref<UserDto | null>(readStoredUser())
  const loading = ref(false)
  const error = ref<ApiError | null>(null)
  /** Set when a session was terminated by a failed refresh, so login can explain why. */
  const sessionExpired = ref(false)

  const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null)
  const permissions = computed<string[]>(() => user.value?.permissions ?? [])

  function hasPermission(permission: Permission): boolean {
    return permissions.value.includes(permission)
  }

  function hasAnyPermission(required: readonly Permission[]): boolean {
    if (required.length === 0) return true
    return required.some((permission) => permissions.value.includes(permission))
  }

  function applySession(result: AuthResultDto): void {
    accessToken.value = result.accessToken
    refreshToken.value = result.refreshToken
    user.value = result.user
    sessionExpired.value = false
    writeStorage(ACCESS_TOKEN_KEY, result.accessToken)
    writeStorage(REFRESH_TOKEN_KEY, result.refreshToken)
    writeStorage(USER_KEY, JSON.stringify(result.user))
  }

  function clearSession(): void {
    accessToken.value = null
    refreshToken.value = null
    user.value = null
    writeStorage(ACCESS_TOKEN_KEY, null)
    writeStorage(REFRESH_TOKEN_KEY, null)
    writeStorage(USER_KEY, null)
  }

  async function login(email: string, password: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const result = await authApi.login({ email, password })
      applySession(result)
    } catch (caught) {
      const apiError = caught instanceof ApiError ? caught : new ApiError({ message: String(caught), kind: 'UNKNOWN' })
      error.value = apiError
      throw apiError
    } finally {
      loading.value = false
    }
  }

  /** Best-effort server-side invalidation; the local session is always cleared. */
  async function logout(): Promise<void> {
    const token = refreshToken.value
    clearSession()
    if (!token) return
    try {
      await authApi.logout(token)
    } catch {
      /* the local session is already gone; a failed logout call must not block the UI */
    }
  }

  /** Re-reads `/auth/me` so permissions cannot go stale across a long-lived tab. */
  async function loadCurrentUser(): Promise<void> {
    if (!accessToken.value) return
    const fresh = await authApi.me()
    user.value = fresh
    writeStorage(USER_KEY, JSON.stringify(fresh))
  }

  /** Changes the password and adopts the new token pair the server issues for this tab. */
  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const result = await authApi.changePassword({ currentPassword, newPassword })
    applySession(result)
  }

  function markSessionExpired(): void {
    sessionExpired.value = true
    clearSession()
  }

  /** The session every tab shares: localStorage is the source of truth across tabs. */
  function sharedSession(): AuthResultDto | null {
    const access = readStorage(ACCESS_TOKEN_KEY)
    const refresh = readStorage(REFRESH_TOKEN_KEY)
    const storedUser = readStoredUser()
    if (!access || !refresh || !storedUser) return null
    return { accessToken: access, refreshToken: refresh, expiresInSeconds: 0, user: storedUser }
  }

  // Wire the axios client to this store: one refresh attempt, then hard logout.
  registerTokenBridge({
    getAccessToken: () => accessToken.value,
    getRefreshToken: () => refreshToken.value,
    onRefreshed: (result) => applySession(result),
    onSessionLost: () => markSessionExpired(),
    getSharedSession: () => sharedSession(),
  })

  // Another tab refreshed, signed in or signed out: follow it, so this tab never keeps
  // presenting a token the server has already retired.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === ACCESS_TOKEN_KEY) accessToken.value = event.newValue
      else if (event.key === REFRESH_TOKEN_KEY) refreshToken.value = event.newValue
      else if (event.key === USER_KEY) user.value = readStoredUser()
      else if (event.key === null) {
        // localStorage.clear() somewhere else.
        accessToken.value = readStorage(ACCESS_TOKEN_KEY)
        refreshToken.value = readStorage(REFRESH_TOKEN_KEY)
        user.value = readStoredUser()
      }
    })
  }

  return {
    accessToken,
    refreshToken,
    user,
    loading,
    error,
    sessionExpired,
    isAuthenticated,
    permissions,
    hasPermission,
    hasAnyPermission,
    login,
    logout,
    loadCurrentUser,
    changePassword,
    applySession,
    clearSession,
    markSessionExpired,
  }
})
